import { TableTagCell } from "@src/components/data-components/DocumentationCollection/cells/CustomFieldTagCell";
import { formatTagName } from "data-hooks/company/tags/tags.utils";
import { t } from "i18next";
import { DataTableData, DataTableHeader } from "../../../../../components/deprecated/DataTable/DataTable";
import { IconNames } from "../../../../../components/deprecated/Icon/IconNames";
import { Label } from "../../../../../components/deprecated/Label/Label";
import { StatusTag } from "../../../../../components/deprecated/StatusTag/StatusTag";
import { FrequencyUtilities } from "../../../../../utils/frequencyUtilities";
import { StringUtilities } from "../../../../../utils/stringUtilities";
import { ContractorGroupData } from "../../../ContractorGroup/ContractorGroupData";
import { DeviationPriority } from "../../../Deviation/DeviationPriority";
import { RoutineData } from "../../../Routines/RoutineData";
import { UserData } from "../../../User/UserData";
import {
  DataObjectRawTagField,
  DeviationDataPriorityField,
  DeviationDataVisibilityField,
} from "../../DataObjectField/CustomFields/DeviationDataFields";
import { FirebaseDataCompanyField } from "../../DataObjectField/CustomFields/FirebaseDataCompanyField";
import { FirebaseDataContractorField } from "../../DataObjectField/CustomFields/FirebaseDataContractorField";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataUserField } from "../../DataObjectField/CustomFields/FirebaseDataUserField";
import { PropertyDataFloorsField } from "../../DataObjectField/CustomFields/PropertyFields";
import {
  RoutineDataCreateDaysBeforeField,
  RoutineDataDeadlineDelayField,
  RoutineDataFrequencyField,
} from "../../DataObjectField/CustomFields/RoutineDataFields";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { LocalData } from "../../LocalData";
import { getRoutineActionsForData } from "./RoutineDataActions";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataCollectionItemFields } from "../../DataObjectField/CustomFields/FirebaseDataCollectionItemFields";
import { GenericCollectionItemType } from "../Property/CollectionItemDataHelper";
import { CompanyCollectionData } from "@src/to-be-removed/LocalData/Company/Collections/CompanyCollectionData";
import { PropertyCollectionData } from "@src/to-be-removed/LocalData/Property/Collections/PropertyCollectionData";

export class RoutineDataHelper extends DataObjectHelper<RoutineData> {
  id = () => "routine";
  displayName = () => t("main.routine");
  displayNamePlural = () => t("main.routines");

  companies = new FirebaseDataCompanyField<RoutineData>("companies", t("main.companies"), (routine) => routine.companies, IconNames.COMPANY, true);
  properties = new FirebaseDataPropertyField<RoutineData>(
    "properties",
    t("main.properties"),
    (routine) => routine.properties.map((p) => p.data),
    IconNames.PROPERTY,
    true
  );
  title = new FirebaseDataStringField<RoutineData>("title", t("main.title"), (routine) => routine.title, "info");
  description = new FirebaseDataStringField<RoutineData>("description", t("main.description"), (routine) => routine.description, "edit", false, true);
  active = new FirebaseDataBooleanField<RoutineData>("active", t("main.active"), (routine) => routine.active, "label");
  startDate = new FirebaseDataDateField<RoutineData>("startDate", t("main.startDate"), (routine) => routine.startDate, "arrow_forward");
  createDaysBefore = new RoutineDataCreateDaysBeforeField(
    "createDaysBefore",
    t("main.isCreated"),
    (routine) => routine.createDaysBefore,
    "call_made"
  );
  deadlineDelay = new RoutineDataDeadlineDelayField("deadlineDelay", t("main.deadline"), (routine) => routine.deadlineDelay, "keyboard_tab");
  frequency = new RoutineDataFrequencyField<RoutineData>("frequency", t("main.frequency"), (routine) => routine.frequency, "repeat");
  tags = new DataObjectRawTagField<RoutineData>("tags", t("main.tags"), (routine) => routine.tags, IconNames.TAG, true);
  taskTypeID = new FirebaseDataStringField<RoutineData>("taskTypeID", t("main.taskType"), (routine) => routine.taskTypeID, "label");
  floors = new PropertyDataFloorsField<RoutineData>("floors", t("main.floors"), (routine) => routine.floors, "stairs", true);
  placeDescription = new FirebaseDataStringField<RoutineData>(
    "placeDescription",
    t("main.placeDescription"),
    (routine) => routine.placeDescription,
    "place"
  );
  visibility = new DeviationDataVisibilityField<RoutineData>("visibility", t("main.visibility"), (routine) => routine.visibility, "visibility");
  archived = new FirebaseDataBooleanField<RoutineData>(
    "archived",
    t("main.isArchivedText"),
    (routine) => routine.archived || (routine.properties.length > 0 && routine.properties.every((property) => property.data.archived)),
    "archive",
    false
  );
  contractorGroups = new FirebaseDataContractorField<RoutineData>(
    "contractorGroups",
    t("main.contractorGroups"),
    (routine) => routine.contractorGroups,
    "label",
    true
  );
  responsibles = new FirebaseDataUserField<RoutineData>("responsibles", t("main.responsibles"), (routine) => routine.responsibles, "label", true);
  executors = new FirebaseDataUserField<RoutineData>("executors", t("main.executors"), (routine) => routine.executors, "label", true);
  priority = new DeviationDataPriorityField<RoutineData>("priority", t("main.priority"), (routine) => routine.priority, IconNames.PRIORITY);

  override getAllDatas(): RoutineData[] {
    return LocalData.routines.documents.filter((routine) => !routine.deleted);
  }

  loadAll(): Promise<any> {
    return new Promise((resolve) => {
      LocalData.routinesLoader.loadRoutines(() => {
        resolve(
          LocalData.routines.documents.filter((routine) =>
            routine.properties.some((property) => LocalData.currentUser.properties.includes(property.data))
          )
        );
      });
    });
  }

  override allFields() {
    return [
      this.companies,
      this.properties,
      this.title,
      this.description,
      this.active,
      this.startDate,
      this.createDaysBefore,
      this.deadlineDelay,
      this.frequency,
      this.tags,
      this.floors,
      this.placeDescription,
      this.contractorGroups,
      this.responsibles,
      this.executors,
      this.priority,
      this.archived,
    ];
  }

  getRelativeFields(): DataObjectField<any, RoutineData>[] {
    const fields: DataObjectField<any, RoutineData>[] = [];

    for (const company of LocalData.currentUser.companies) {
      for (const collection of company.collections.documents) {
        // Skip collections that are already added from another company
        if (fields.some((field) => field.key === collection.id)) continue;

        const field = new FirebaseDataCollectionItemFields<RoutineData>(
          company.id,
          undefined,
          collection.id,
          "collectionItem-" + collection.id,
          collection.name.translate(),
          () => undefined, // this data fetch is difficult in old DataObjectHelper because it doesn't know what property it is for
          "flag"
        );
        fields.push(field);
      }
    }

    return fields;
  }

  getAllDataTableDatas(documents: RoutineData[]): DataTableData[] {
    return documents
      .filter((routine) => routine.properties.length === 0 || !routine.properties.every((property) => property.data.deleted))
      .map((routine) => {
        const tags = routine.tags ?? [];

        function getDataNames(datas: (UserData | ContractorGroupData)[]): string | Promise<string> {
          if (datas.some((data) => !data.loaded)) {
            return new Promise((resolve) =>
              Promise.all(datas.filter((data) => !data.loaded).map((data) => data.load())).then(() =>
                resolve(datas.map((data) => data.name).join(", "))
              )
            );
          } else {
            return datas.map((data) => data.name).join(", ");
          }
        }

        return {
          key: routine.id,
          cells: {
            title: routine.title,
            tags: {
              element: <TableTagCell tagIDs={tags} />,
              rawString: routine.tags
                ?.map((tagID) => {
                  const tag = LocalData.getTagWithId(tagID);
                  return tag ? formatTagName(tag) : "";
                })
                .join(" "),
            },
            description: routine.description,
            startDate: routine.startDate,
            properties:
              routine.properties.length > 0
                ? StringUtilities.createString(
                    routine.properties
                      .map((p) => p.data)
                      .filter((p) => p.members.hasData(LocalData.currentUser.id))
                      .map((p) => p.name)
                      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
                    5
                  )
                : t("main.none"),
            frequency: routine.frequency ? FrequencyUtilities.convertFrequencyToString(routine.frequency) : t("main.noFrequency"),
            active: {
              element: <StatusTag color={routine.active ? "green" : "red"} text={routine.active ? t("main.active") : t("main.notActive")} />,
              rawString: routine.active ? t("main.active") : t("main.notActive"),
            },
            priority: {
              element: (
                <StatusTag
                  text={routine.priority?.name ?? t("main.notSpecified")}
                  color={routine.priority === DeviationPriority.HIGH ? "red" : routine.priority === DeviationPriority.MEDIUM ? "orange" : "green"}
                />
              ),
              rawString: routine.priority?.name ?? t("main.notSpecified"),
            },
            floors: routine.floors.join(", "),
            placeDescription: routine.placeDescription,
            contractorGroups: getDataNames(routine.contractorGroups),
            responsibles: getDataNames(routine.responsibles),
            executors: getDataNames(routine.executors),
            files: {
              element: <Label type="paragraph" text={`${routine.files.length} ${routine.files.length === 1 ? t("main.file") : t("main.files")}`} />,
              rawString: `${routine.files.length} ${routine.files.length === 1 ? t("main.file") : t("main.files")}`,
              rawData: routine.files,
            },
          },
        };
      });
  }

  override getAllDataTableHeaders(): DataTableHeader[] {
    return [
      {
        key: "title",
        title: t("main.title"),
      },
      {
        key: "description",
        title: t("main.description"),
      },
      {
        key: "startDate",
        title: t("main.startDate"),
      },
      {
        key: "properties",
        title: t("main.properties"),
      },
      {
        key: "frequency",
        title: t("main.frequency"),
      },
      {
        key: "tags",
        title: t("main.tags"),
      },
      { key: "active", title: t("main.status") },
      {
        key: "priority",
        title: t("main.priority"),
      },
      {
        key: "floors",
        title: t("main.floors"),
      },
      {
        key: "placeDescription",
        title: t("main.placeDescription"),
      },
      { key: "contractorGroups", title: t("main.contractorGroups") },
      { key: "responsibles", title: t("main.responsibles") },
      { key: "executors", title: t("main.executors") },
      { key: "files", title: t("main.files") },
    ];
  }

  getLinkTo(key: string): string | undefined {
    return `/routines/${key}`;
  }

  override getStandardHeaderElements(): string[] {
    return ["title", "properties", "tags", "frequency", "contractorGroups", "responsibles", "executors", "active"];
  }

  override getActionsForData = (data: RoutineData, selectedDatas: RoutineData[] = []): DataObjectAction<RoutineData>[] => {
    return getRoutineActionsForData(this, data, selectedDatas);
  };
}
